import React, { useLayoutEffect, useRef } from "react";
import { BrandSurface } from "../../design-system/ui/BrandSurface/BrandSurface";
import { JobDetailTemplate } from "../../design-system/ui/templates/JobDetailTemplate";

/** What the clicked SRP card already knows about the job, captured at click time. */
export interface JobPreviewSeed {
  jobId: string;
  /** The job page URL the card links to (what Link.tsx hands to navigate()). */
  href: string;
  title: string;
  companyName: string | null;
  /** The stored brand colour (resolved like the job page does), so the rail picks the same colour as the real BrandSurface rail in every theme. Seeds without it fall back to panelColor / panelOn. */
  brandColour?: string | null;
  /** adjustBrandColor(...).panel / .on, today's values; only used when `brandColour` is absent. */
  panelColor: string;
  panelOn: string;
  /** The card's already-loaded logo image, so the shell shows it without a new request. */
  logoSrc: string | null;
  /** Fallback when the card logo isn't an <img> (initials tile): a clone of the card's logo DOM. */
  logoNode: Node | null;
}

const BAR = "animate-pulse rounded-full bg-jc-skeleton";
const RAIL_BAR = "animate-pulse rounded-full bg-jc-on-brand-fallback/25";

/**
 * Instant stand-in for the job page while its data loads (see utils/view-transitions/
 * job-preview-shell.ts). Renders the real page's own JobDetailTemplate and BrandSurface rail (the
 * same wrappers, rail/logo/company name and display-type h1 as BrandRail and the template),
 * so the card morph lands on the same boxes the real page then renders into, and only the
 * skeleton areas visibly change when it swaps in. Decorative only: aria-hidden, no strings of its
 * own, every text it shows is the job's own data.
 */
export function JobPreviewShell({ seed, topOffset }: { seed: JobPreviewSeed; topOffset: number }) {
  const logoFallbackRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const host = logoFallbackRef.current;
    if (!host || seed.logoSrc || !seed.logoNode) return;
    host.replaceChildren(seed.logoNode.cloneNode(true));
  }, [seed]);

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 z-(--jc-z-sticky) overflow-x-hidden overflow-y-scroll bg-jc-page"
      // Covers everything under the (higher z-index) site header; content starts where the job
      // page's own header ends, which can be lower than the header currently on screen.
      // overflow-y: scroll gives the shell the same scrollbar gutter the (long, window-scrolled)
      // job page has, so with classic scrollbars (Windows/Linux) the content is centred in the
      // same width and doesn't shift sideways on the swap; overlay scrollbars take no width either way.
      style={{ paddingTop: topOffset }}
      data-job-preview-shell=""
    >
      {/* MinimalChrome's <main> padding, then +Page.tsx's wrappers and JobDetailPageV2EditorialRail's main. */}
      <div className="min-w-0 pt-1 sm:pt-1 md:pt-2 sm:px-0 md:px-2 lg:px-8">
        <div className="min-h-screen bg-jc-page">
          <div className="pb-6">
            <main>
              <div className="jc-job-page jc-vt-on">
                {/* The real page's template, filled with placeholders: every box (rail, title, facts
                    card, side column) lands where the job page then renders, and the title is in the
                    same display type, so nothing jumps or changes font on the swap. */}
                <JobDetailTemplate
                  cardClassName="jc-vt-card"
                  breadcrumbs={
                    <div className="flex flex-col">
                      {/* BackToResultsLink's box; the text baseline (not the bar's) sets the line. */}
                      <div className="mb-2 inline-flex items-center gap-1 self-start px-1 text-sm font-normal sm:px-0">
                        <span className="relative inline-block w-36">
                          &nbsp;
                          <span className={`${BAR} absolute inset-x-0 top-[3px] h-3.5`} />
                        </span>
                      </div>
                      {/* Breadcrumb trail and language switcher row */}
                      <div className="mb-4 flex h-[30px] flex-row items-center justify-between gap-2 px-1 sm:gap-4 sm:px-0">
                        <span className={`${BAR} h-3.5 w-56 max-w-[60%]`} />
                        <span className={`${BAR} h-[30px] w-40`} />
                      </div>
                    </div>
                  }
                  rail={
                    <BrandSurface
                      color={seed.brandColour ?? null}
                      shape="rail"
                      className="jc-vt-rail relative flex flex-none flex-col items-center box-border rounded-none max-md:p-4 md:w-full! md:rounded-none lg:items-stretch lg:py-6"
                    >
                      <div className="w-full flex flex-col items-center lg:sticky lg:top-24 lg:items-stretch">
                        <div className="relative flex w-full flex-none flex-col items-center text-center">
                          <div className="flex max-w-full items-center justify-center gap-3 lg:w-full lg:flex-col lg:gap-0">
                            <div className="jc-vt-logo flex h-11 w-11 lg:h-16 lg:w-16 flex-none items-center justify-center rounded-jc-container bg-jc-logo-surface p-1.5 lg:p-3.5 box-border overflow-hidden">
                              {seed.logoSrc ? (
                                <img
                                  src={seed.logoSrc}
                                  alt=""
                                  className="h-full w-full object-contain"
                                  decoding="sync"
                                />
                              ) : (
                                <div ref={logoFallbackRef} className="flex h-full w-full items-center justify-center" />
                              )}
                            </div>
                            {seed.companyName ? (
                              <div className="min-w-0 break-words text-left type-subheading leading-tight lg:mt-3 lg:w-full lg:text-center">
                                {seed.companyName}
                              </div>
                            ) : null}
                          </div>
                          {/* Apply (52px, as ApplyButton compact) and the deadline line under it. */}
                          <span className={`${RAIL_BAR} mt-3 lg:mt-5 h-[52px] w-full`} />
                          <span className={`${RAIL_BAR} mt-3 h-3.5 w-28`} />
                        </div>
                      </div>
                    </BrandSurface>
                  }
                  title={<span className="jc-vt-title block">{seed.title}</span>}
                  actions={<span className={`${BAR} h-10 w-[202px]`} />}
                  notices={
                    <>
                      <span className={`${BAR} h-3.5 w-64 max-w-full`} />
                      {/* The key facts card (pay header, then the facts grid). */}
                      <div className="overflow-hidden rounded-jc-container border border-jc-card-border bg-jc-surface shadow-jc-sm">
                        <div className="flex flex-col gap-3 border-b border-jc-border px-5 py-4 md:px-6">
                          <span className={`${BAR} h-7 w-44`} />
                          <span className={`${BAR} h-3.5 w-56`} />
                        </div>
                        <div className="grid grid-cols-1 gap-x-8 gap-y-4 px-5 py-5 sm:grid-cols-2 md:px-6">
                          {[0, 1, 2, 3].map((i) => (
                            <div key={i} className="flex flex-col gap-2">
                              <span className={`${BAR} h-3 w-24`} />
                              <span className={`${BAR} h-4 w-36`} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  }
                  description={{
                    title: <span className={`${BAR} inline-block h-6 w-40 align-middle`} />,
                    content: (
                      <div className="flex flex-col gap-3">
                        <span className={`${BAR} h-3.5 w-full`} />
                        <span className={`${BAR} h-3.5 w-[94%]`} />
                        <span className={`${BAR} h-3.5 w-[88%]`} />
                        <span className={`${BAR} h-3.5 w-[72%]`} />
                      </div>
                    ),
                  }}
                  side={
                    <div className="hidden flex-col gap-4 lg:flex">
                      <span className={`${BAR} h-5 w-32`} />
                      {[0, 1, 2].map((i) => (
                        <span key={i} className="block h-48 animate-pulse rounded-jc-container bg-jc-skeleton" />
                      ))}
                    </div>
                  }
                />
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
